import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getDeliveryNotes, deleteDeliveryNote, convertDeliveryToInvoice, getDeliveryNotePdfUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Plus, Search, MoreVertical, Trash2, ArrowRightCircle, Truck,
  Download, X as XIcon, Users, Package, CheckCircle2, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import InvoiceDesignBanner from '../../components/invoices/InvoiceDesignBanner';
import PageHelpButton from '../../components/help/PageHelpButton';
import AiCreateButton from '../../components/ai/AiCreateButton';
import { StatCard, FilterChip, FilterRow, FiltersCard, SurfaceCard, ResetFiltersButton } from '../../components/ui/filters-toolkit';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const STATUS_LABELS = { draft: 'Borrador', delivered: 'Entregado' };
const STATUS_CHIPS = [
  { value: 'all', label: 'Todos', dot: 'bg-stone-400' },
  { value: 'draft', label: 'Borradores', dot: 'bg-stone-400' },
  { value: 'delivered', label: 'Entregados', dot: 'bg-emerald-500' },
];

const getYear = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};

const StatusBadge = ({ status }) => (
  <span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span>
);

export const DeliveryNotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getDeliveryNotes();
        if (!cancelled) setNotes(data);
      } catch {
        toast.error('Error al cargar albaranes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    try { const { data } = await getDeliveryNotes(); setNotes(data); } catch {}
  };

  const years = useMemo(() => {
    const set = new Set(notes.map((n) => getYear(n.issue_date)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [notes]);

  const clients = useMemo(() => {
    const set = new Set(notes.map((n) => n.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [notes]);

  const filteredNotes = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return notes.filter((note) => {
      if (q) {
        const hay = `${note.delivery_number || ''} ${note.client_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && note.status !== statusFilter) return false;
      if (yearFilter !== 'all' && getYear(note.issue_date) !== parseInt(yearFilter, 10)) return false;
      if (clientFilter !== 'all' && note.client_name !== clientFilter) return false;
      return true;
    });
  }, [notes, searchTerm, statusFilter, yearFilter, clientFilter]);

  const stats = useMemo(() => {
    const acc = { total: 0, delivered: 0, draftCount: 0, count: filteredNotes.length };
    filteredNotes.forEach((n) => {
      const t = Number(n.total) || 0;
      acc.total += t;
      if (n.status === 'delivered') acc.delivered += t;
      else if (n.status === 'draft') acc.draftCount += 1;
    });
    return acc;
  }, [filteredNotes]);

  const activeFiltersCount = [
    statusFilter !== 'all', yearFilter !== 'all', clientFilter !== 'all', searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setYearFilter('all'); setClientFilter('all');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este albarán?')) return;
    try { await deleteDeliveryNote(id); toast.success('Albarán eliminado'); reload(); }
    catch { toast.error('Error al eliminar albarán'); }
  };

  const handleConvertToInvoice = async (id) => {
    try { await convertDeliveryToInvoice(id); toast.success('Albarán convertido a factura'); reload(); }
    catch { toast.error('Error al convertir albarán'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Albaranes</h1>
            <p className="text-stone-500 text-sm mt-1">Gestiona tus albaranes de entrega</p>
          </div>
          <PageHelpButton moduleKey="delivery-notes" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Link to="/delivery-notes/new" className="w-full sm:w-auto">
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="create-delivery-note-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Albarán
            </Button>
          </Link>
          <AiCreateButton entity="delivery_note" className="w-full sm:w-auto" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="delivery-stats">
        <StatCard
          label="Albaranes"
          value={stats.count}
          sub="Filtrados"
          icon={Package}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-delivery-count"
        />
        <StatCard
          label="Importe total"
          value={formatCurrency(stats.total)}
          sub={`Entregado: ${formatCurrency(stats.delivered)}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="stat-delivery-total"
        />
        <StatCard
          label="Entregados"
          value={formatCurrency(stats.delivered)}
          sub={stats.total > 0 ? `${Math.round((stats.delivered / stats.total) * 100)}% del total` : '—'}
          icon={CheckCircle2}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="stat-delivery-delivered"
        />
      </div>

      <InvoiceDesignBanner entityLabel="albarán" />

      <FiltersCard testid="delivery-filters-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por número o cliente…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-delivery-notes-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600">
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="relative lg:w-64">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="delivery-client-filter">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {activeFiltersCount > 0 && (
            <ResetFiltersButton count={activeFiltersCount} onClick={resetFilters} testid="delivery-reset-filters" />
          )}
        </div>

        {years.length > 0 && (
          <FilterRow label="Año" testid="delivery-year-filters">
            <FilterChip active={yearFilter === 'all'} onClick={() => setYearFilter('all')} testid="delivery-year-chip-all">Todos</FilterChip>
            {years.map((y) => (
              <FilterChip key={y} active={yearFilter === String(y)} onClick={() => setYearFilter(String(y))} testid={`delivery-year-chip-${y}`}>{y}</FilterChip>
            ))}
          </FilterRow>
        )}

        <FilterRow label="Estado" testid="delivery-status-filters">
          {STATUS_CHIPS.map((s) => (
            <FilterChip key={s.value} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} dot={s.dot} testid={`delivery-status-chip-${s.value}`}>
              {s.label}
            </FilterChip>
          ))}
        </FilterRow>
      </FiltersCard>

      {filteredNotes.length > 0 ? (
        <SurfaceCard>
          <div className="overflow-x-auto">
            <table className="w-full invoice-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Número</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Fecha</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredNotes.map((note) => (
                  <tr key={note.id} className="hover:bg-orange-50/40 transition-colors" data-testid={`delivery-note-row-${note.id}`}>
                    <td className="px-4 py-4"><span className="font-mono text-sm text-[#FF6B4A]">{note.delivery_number}</span></td>
                    <td className="px-4 py-4 text-sm">{note.client_name}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{note.issue_date}</td>
                    <td className="px-4 py-4"><StatusBadge status={note.status} /></td>
                    <td className="px-4 py-4 text-right font-mono text-sm font-medium">{formatCurrency(note.total)}</td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-stone-100 rounded-xl"><MoreVertical className="w-4 h-4 text-stone-500" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleConvertToInvoice(note.id)}>
                            <ArrowRightCircle className="w-4 h-4 mr-2" /> Convertir a Factura
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(getDeliveryNotePdfUrl(note.id), '_blank')}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(note.id)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard testid="delivery-empty-state">
          <div className="text-center py-12">
            <Truck className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-900 mb-1">
              {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : 'No hay albaranes'}
            </h3>
            <p className="text-stone-500 mb-4">
              {activeFiltersCount > 0 ? 'Prueba a limpiar algún filtro.' : 'Crea tu primer albarán de entrega'}
            </p>
            {activeFiltersCount > 0 ? (
              <button onClick={resetFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all">
                <XIcon className="w-4 h-4" /> Limpiar filtros
              </button>
            ) : (
              <Link to="/delivery-notes/new">
                <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="create-first-delivery-note-button">
                  <Plus className="w-4 h-4 mr-2" /> Crear Albarán
                </Button>
              </Link>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

export default DeliveryNotesPage;
